/**
 * Communes : les pages locales de la zone d'intervention.
 *
 * Leur garde-fou diffère de celui des prestations, et l'écran le dit : 150 mots
 * de contenu **local** suffisent, et une réalisation publiée dans la commune
 * dispense même de ce seuil — une page qui montre un chantier fait sur place
 * vaut mieux qu'une page qui en parle.
 *
 * La réponse directe d'une commune ne se saisit pas ici : elle vit dans
 * `api/src/db/contenu/communes-contenu.ts`, que la landing lit au build. Le
 * compteur de mots en tient compte, mais l'écran ne prétend pas la modifier.
 */
import Alert from "@mui/material/Alert";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ContenuAtelier,
  PiedFormulaire,
  Section,
  type Selection,
  champsModifies,
  erreurChamp,
  useFiche,
} from "../composants/ContenuAtelier";
import {
  type Commune,
  useCommunes,
  useEnregistrer,
  useQualite,
  useSupprimer,
} from "../composants/ContenuDonnees";
import { AlertesQualite, ResumeQualite, compterMotsHtml } from "../composants/ContenuQualite";
import { ChampsSeo } from "../composants/ContenuSeo";
import { EditeurRiche } from "../composants/EditeurRiche";

/**
 * Les coordonnées et la distance restent des chaînes tant qu'on les saisit :
 * un champ où l'on vient de taper « 49. » n'est pas encore un nombre, et le
 * convertir à chaque frappe ramènerait le curseur au début ou effacerait la
 * saisie en cours.
 */
type Brouillon = {
  slug: string;
  nom: string;
  codePostal: string;
  codeInsee: string;
  lat: string;
  lng: string;
  distanceKm: string;
  contenu: string;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
  ordre: number;
};

const VIDE: Brouillon = {
  slug: "",
  nom: "",
  codePostal: "",
  codeInsee: "",
  lat: "",
  lng: "",
  distanceKm: "",
  contenu: "",
  metaTitle: "",
  metaDescription: "",
  isPublished: false,
  ordre: 0,
};

/** Champs que l'API attend en nombre. */
const NUMERIQUES = ["lat", "lng", "distanceKm"] as const;

function depuis(commune: Commune): Brouillon {
  return {
    slug: commune.slug,
    nom: commune.nom,
    codePostal: commune.codePostal,
    codeInsee: commune.codeInsee,
    lat: String(commune.lat),
    lng: String(commune.lng),
    distanceKm: String(commune.distanceKm),
    contenu: commune.contenu,
    metaTitle: commune.metaTitle,
    metaDescription: commune.metaDescription,
    isPublished: commune.isPublished,
    ordre: commune.ordre,
  };
}

/**
 * Convertit les champs numériques juste avant l'envoi. Une valeur illisible est
 * transmise telle quelle : c'est à zod de la refuser avec son message, pas à
 * l'interface de la remplacer par un zéro silencieux qui déplacerait la commune
 * au milieu de l'Atlantique.
 */
function pourApi(champs: Record<string, unknown>): Record<string, unknown> {
  const converti: Record<string, unknown> = { ...champs };
  for (const cle of NUMERIQUES) {
    if (!(cle in converti)) continue;
    const brut = String(converti[cle]).trim().replace(",", ".");
    const nombre = Number(brut);
    if (brut !== "" && Number.isFinite(nombre)) converti[cle] = nombre;
  }
  return converti;
}

const cheminPublic = (slug: string) => `/renovation-interieure-${slug}/`;

export function ContenuCommunes() {
  const communes = useCommunes();
  const qualite = useQualite();
  const enregistrement = useEnregistrer<Commune>("communes");
  const suppression = useSupprimer("communes");
  const fiche = useFiche<Brouillon>(VIDE);

  const liste = communes.data ?? [];
  const courante =
    typeof fiche.selection === "number"
      ? liste.find((commune) => commune.id === fiche.selection)
      : undefined;

  const enDefaut = new Set(
    (qualite.data?.pages ?? [])
      .filter((page) => page.type === "commune" && page.manquements.length > 0)
      .map((page) => page.chemin),
  );

  function selectionner(cible: Selection) {
    enregistrement.reset();
    suppression.reset();

    if (cible === null) return fiche.fermer();
    if (cible === "nouveau") return fiche.ouvrir("nouveau", VIDE);

    const commune = liste.find((element) => element.id === cible);
    if (commune) fiche.ouvrir(cible, depuis(commune));
  }

  function enregistrer() {
    const id = typeof fiche.selection === "number" ? fiche.selection : null;
    const champs =
      id === null ? { ...fiche.brouillon } : champsModifies(fiche.brouillon, fiche.reference);
    if (Object.keys(champs).length === 0) return;

    enregistrement.mutate(
      { id, champs: pourApi(champs) },
      {
        onSuccess: (commune) => {
          if (commune) fiche.confirmer(commune.id, depuis(commune));
        },
      },
    );
  }

  function supprimer() {
    if (typeof fiche.selection !== "number") return;
    suppression.mutate(fiche.selection, { onSuccess: () => fiche.fermer() });
  }

  const erreur = enregistrement.error ?? suppression.error ?? null;
  const brouillon = fiche.brouillon;

  return (
    <ContenuAtelier
      intro="Une page par commune desservie. Elle n'a d'intérêt que si elle dit quelque chose de local : sans contenu propre ni réalisation sur place, elle vaut moins qu'une absence de page."
      entete={<ResumeQualite filtre="commune" />}
      entrees={liste.map((commune) => ({
        id: commune.id,
        libelle: commune.nom,
        secondaire: `${commune.codePostal} · ${commune.distanceKm} km`,
        publie: commune.isPublished,
        enDefaut: enDefaut.has(cheminPublic(commune.slug)),
      }))}
      selection={fiche.selection}
      surSelection={selectionner}
      libelleNouveau="Nouvelle commune"
      modifie={fiche.modifie}
      chargement={communes.isPending}
      erreur={communes.error ?? null}
      invite="Choisissez une commune à modifier, ou ajoutez-en une."
    >
      <Stack spacing={3}>
        <AlertesQualite
          chemin={courante ? cheminPublic(courante.slug) : null}
          type="commune"
          motsBrouillon={compterMotsHtml(brouillon.contenu)}
          motsEnregistres={courante ? compterMotsHtml(courante.contenu) : 0}
        />

        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={4}>
            <Section titre="Identité">
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Nom de la commune"
                  value={brouillon.nom}
                  onChange={(evenement) => fiche.modifier({ nom: evenement.target.value })}
                  error={erreurChamp(erreur, "nom") !== undefined}
                  helperText={erreurChamp(erreur, "nom")}
                />
                <TextField
                  fullWidth
                  label="Code postal"
                  value={brouillon.codePostal}
                  onChange={(evenement) => fiche.modifier({ codePostal: evenement.target.value })}
                  error={erreurChamp(erreur, "codePostal") !== undefined}
                  helperText={erreurChamp(erreur, "codePostal")}
                />
                <TextField
                  fullWidth
                  label="Code INSEE"
                  value={brouillon.codeInsee}
                  onChange={(evenement) => fiche.modifier({ codeInsee: evenement.target.value })}
                  error={erreurChamp(erreur, "codeInsee") !== undefined}
                  helperText={erreurChamp(erreur, "codeInsee")}
                />
              </Stack>

              <TextField
                fullWidth
                label="Slug (URL)"
                value={brouillon.slug}
                onChange={(evenement) => fiche.modifier({ slug: evenement.target.value })}
                error={erreurChamp(erreur, "slug") !== undefined}
                helperText={
                  erreurChamp(erreur, "slug") ??
                  "Il complète l'adresse de la page ; le modifier après publication casse les liens existants."
                }
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        delsa.pro/renovation-interieure-
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Latitude"
                  value={brouillon.lat}
                  onChange={(evenement) => fiche.modifier({ lat: evenement.target.value })}
                  error={erreurChamp(erreur, "lat") !== undefined}
                  helperText={erreurChamp(erreur, "lat")}
                />
                <TextField
                  fullWidth
                  label="Longitude"
                  value={brouillon.lng}
                  onChange={(evenement) => fiche.modifier({ lng: evenement.target.value })}
                  error={erreurChamp(erreur, "lng") !== undefined}
                  helperText={erreurChamp(erreur, "lng")}
                />
                <TextField
                  fullWidth
                  label="Distance (km)"
                  value={brouillon.distanceKm}
                  onChange={(evenement) => fiche.modifier({ distanceKm: evenement.target.value })}
                  error={erreurChamp(erreur, "distanceKm") !== undefined}
                  helperText={erreurChamp(erreur, "distanceKm")}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Ordre"
                  value={brouillon.ordre}
                  onChange={(evenement) =>
                    fiche.modifier({ ordre: Number(evenement.target.value) || 0 })
                  }
                />
              </Stack>

              <FormControlLabel
                control={
                  <Switch
                    checked={brouillon.isPublished}
                    onChange={(evenement) =>
                      fiche.modifier({ isPublished: evenement.target.checked })
                    }
                  />
                }
                label="Publier la page de cette commune"
              />
              <Typography variant="caption" color="text.secondary">
                Une commune non publiée reste desservie et citée dans la zone d'intervention ; elle
                n'a simplement pas de page dédiée.
              </Typography>
            </Section>

            <Section titre="Contenu local">
              <Alert severity="info">
                Le texte attendu ici est ce qui distingue cette commune des autres : quartiers,
                types de bâti, contraintes rencontrées sur place. Recopier le même paragraphe d'une
                commune à l'autre produit exactement ce que les moteurs sanctionnent.
              </Alert>

              <EditeurRiche
                libelle="Contenu de la page"
                valeur={brouillon.contenu}
                surChangement={(html) => fiche.modifier({ contenu: html })}
                hauteurMin={280}
                aide="Une réalisation publiée dans cette commune dispense du seuil de mots : un chantier montré vaut mieux qu'un paragraphe de plus."
              />
            </Section>

            <Section titre="Référencement">
              <ChampsSeo
                metaTitle={brouillon.metaTitle}
                metaDescription={brouillon.metaDescription}
                surTitre={(metaTitle) => fiche.modifier({ metaTitle })}
                surDescription={(metaDescription) => fiche.modifier({ metaDescription })}
                chemin={cheminPublic(brouillon.slug || "…")}
                erreurTitre={erreurChamp(erreur, "metaTitle")}
                erreurDescription={erreurChamp(erreur, "metaDescription")}
              />
            </Section>
          </Stack>

          <PiedFormulaire
            modifie={fiche.modifie}
            enregistrement={enregistrement.isPending}
            surEnregistrer={enregistrer}
            surSupprimer={typeof fiche.selection === "number" ? supprimer : undefined}
            suppression={suppression.isPending}
            quoi="cette commune"
            erreur={erreur}
            succes={fiche.succes}
          />
        </Paper>
      </Stack>
    </ContenuAtelier>
  );
}
